import type { LucideIcon } from 'lucide-react';
import { ClipboardCheck, CopyCheck, Glasses, LayoutList, Lock, Network, Scale, Sparkles } from 'lucide-react';

export interface LensHint {
  Icon: LucideIcon;
  /** The design's short name for the lens ("Certainty", not "Contract Certainty"); absent → the step's own label. */
  label?: string;
  /** One line on what the lens looks for; absent for a lens we do not recognise. */
  description?: string;
}

/**
 * Lens presentation keyed on the step. Steps carry only a label, so the icon
 * and one-line description are matched here — copy from the product design
 * screens, one entry per starter lens in `scripts/pbqa-eval/starter/lenses/`.
 * An unrecognised lens gets a generic icon and no description rather than an
 * invented one.
 */
/** The client's own checklist, whether run as a lens (`lens#client-checklist`) or as audit batches (`audit#…`). */
const CUSTOM_CHECKS: LensHint = {
  Icon: ClipboardCheck,
  label: 'Custom checks',
  description: 'Requirements specific to your organisation',
};

const LENS_HINTS: { match: RegExp; hint: LensHint }[] = [
  { match: /^audit#/, hint: CUSTOM_CHECKS },
  { match: /checklist|custom/i, hint: CUSTOM_CHECKS },
  { match: /clarity/i, hint: { Icon: Glasses, label: 'Clarity', description: 'Ambiguous or incorrect provisions' } },
  {
    match: /consisten/i,
    hint: { Icon: CopyCheck, label: 'Consistency', description: 'Conflicting clauses, dates or terms' },
  },
  {
    match: /certainty/i,
    hint: { Icon: Lock, label: 'Certainty', description: 'Completeness against market standards' },
  },
  { match: /placement/i, hint: { Icon: Network, description: 'Placement structure and claims agreement' } },
  {
    match: /regulat|complian/i,
    hint: { Icon: Scale, label: 'Compliance', description: 'Sanctions and regulatory risk location' },
  },
  {
    match: /structur|composition/i,
    hint: { Icon: LayoutList, label: 'Composition', description: 'Type, layout and section composition' },
  },
];

const GENERIC_LENS: LensHint = { Icon: Sparkles };

/** Icon + label + description for a step, matched on its key first and its label second. */
export const lensHint = (stepKey: string, stepLabel = ''): LensHint =>
  LENS_HINTS.find(({ match }) => match.test(stepKey) || match.test(stepLabel))?.hint ?? GENERIC_LENS;

/** What to call the lens in the UI: the design's name when we know it, the step's own label otherwise. */
export const lensDisplayLabel = (stepKey: string, stepLabel: string | undefined): string | undefined =>
  lensHint(stepKey, stepLabel).label ?? stepLabel;
